import { useEffect, useState } from 'react'

/**
 * Theme, with three states rather than two: light, dark, and following the
 * system. "System" is the default because it is what someone expects before
 * they have expressed a preference, and it must remain a distinct choice — a
 * toggle that silently pins a theme is why apps stop tracking the OS at dusk.
 */
export type Theme = 'light' | 'dark' | 'system'

const KEY = 'staleless-theme'

const read = (): Theme => {
  const stored = localStorage.getItem(KEY)
  return stored === 'light' || stored === 'dark' ? stored : 'system'
}

const paint = (theme: Theme): void => {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(read)

  useEffect(() => {
    paint(theme)
    if (theme === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, theme)
  }, [theme])

  return [theme, setTheme]
}

/** Applied before React mounts, so there is no flash of the wrong theme. */
export function applyThemeEarly(): void {
  paint(read())
}
